import { apiService } from './api.service';
import { peekApi } from '../prerender/apiCache';

export interface Region {
    id: string;
    name: string;
    nameAr: string;
    lat: number;
    lon: number;
    cityCount: number;
}

export interface City {
    id: string;
    regionId: string;
    name: string;
    nameAr: string;
    lat: number;
    lon: number;
    geonameId: string;
}

export const locationService = {
    /** First-render read of the prerendered response (undefined when not prerendered). */
    peekAllCities: (): City[] | undefined => peekApi<City[]>('locations/cities'),

    /**
     * Get all regions.
     */
    async getRegions(): Promise<Region[]> {
        const response = await apiService.get<Region[]>('locations/regions');
        return response.data ?? [];
    },

    /**
     * Get cities in a specific region.
     */
    async getCitiesByRegion(regionId: string): Promise<City[]> {
        const response = await apiService.get<City[]>(`locations/regions/${regionId}/cities`);
        return response.data ?? [];
    },

    /**
     * Get ALL cities (flattened, across all regions).
     * Use this for city-first autocomplete.
     */
    async getAllCities(): Promise<City[]> {
        const response = await apiService.get<City[]>('locations/cities');
        return response.data ?? [];
    },

    /**
     * Get a specific city by ID.
     */
    async getCity(cityId: string): Promise<City> {
        const response = await apiService.get<City>(`locations/cities/${cityId}`);
        return response.data;
    },

    /**
     * Get a specific region by ID.
     */
    async getRegion(regionId: string): Promise<Region> {
        const response = await apiService.get<Region>(`locations/regions/${regionId}`);
        return response.data;
    },
    /**
     * Search for an address or neighborhood using OpenStreetMap Nominatim.
     * Restricted to Morocco.
     */
    async searchAddress(query: string): Promise<Array<{
        place_id: number;
        lat: string;
        lon: string;
        display_name: string;
        type: string;
    }>> {
        if (!query || query.length < 3) return [];

        try {
            // Using OSM Nominatim - Free, requires User-Agent
            // In production, consider a self-hosted instance or Mapbox/Google
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=ma&limit=5&addressdetails=1`;

            const response = await fetch(url, {
                headers: {
                    'Accept-Language': 'fr-FR' // Prefer French for consistency? Or dynamic based on user locale
                }
            });

            if (!response.ok) return [];
            return await response.json();
        } catch (error) {
            console.error('Address search failed', error);
            return [];
        }
    },
};
